/**
 * The site's own search: the icon in the masthead opens a dialog, the dialog asks the backend
 * while a visitor types, and Enter takes them to /sok.
 *
 * <p>A `<dialog>` with `showModal()` rather than a div of our own, for the same reason as the
 * lightbox: the browser handles the focus trap, the inertness of the page behind and Escape
 * itself. That is less code, and above all less code that can be wrong.</p>
 *
 * <p>The page works without the script. The button exists only when the search is switched on,
 * and without JavaScript /sok is still a working search form of its own.</p>
 *
 * <p>The arrow keys move <b>real</b> focus between the field and the hits. The first version
 * painted a selection with `data-active` while focus stayed in the field, which left the choice
 * invisible to a screen reader (WCAG 4.1.2) and the marker at 1.16:1 against its surroundings
 * (1.4.11). With real focus the browser's own focus ring is the indicator, and Enter is the
 * link's own behaviour instead of something this script has to imitate.</p>
 */
import { find, require as requireEl } from "./dom";

/** Milliseconds to wait before another letter becomes a request. */
const DEBOUNCE = 180;

/** Below this the query is not a query yet, and the suggestions stay put. */
const MIN_QUERY = 2;

interface Hit {
  title: string;
  summary: string;
  url: string;
  kind: string;
  thumbnail: { url: string; width: number; height: number; altText: string };
}

interface Group {
  heading: string;
  hits: Hit[];
}

interface Results {
  query: string;
  total: number;
  groups: Group[];
  suggested: boolean;
}

let dialog: HTMLDialogElement | null = null;
let input: HTMLInputElement | null = null;
let body: HTMLElement | null = null;
let live: HTMLElement | null = null;
let trigger: HTMLElement | null = null;

let timer = 0;
let inFlight: AbortController | null = null;

/** Built once, on the first click — no markup at all for a visitor who never searches. */
function build(): void {
  const element = document.createElement("dialog");
  element.className = "search-modal";
  element.setAttribute("aria-label", "Sök på sajten");
  element.innerHTML = `
    <form class="search-modal__head" role="search" action="/sok" method="get" data-form>
      <span class="search-modal__field">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" stroke-width="2"/>
          <path d="M15.4 15.4 21 21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <label class="sr-only" for="search-modal-input">Sök på sajten</label>
        <input class="search-modal__input" id="search-modal-input" type="search" name="q"
               placeholder="Sök efter boende, bröllop, konferens …"
               aria-describedby="search-modal-hint"
               autocomplete="off" autocapitalize="none" spellcheck="false" data-input>
      </span>
      <button class="search-modal__close" type="button" data-close>Stäng</button>
    </form>
    <p class="sr-only" id="search-modal-hint">Träffarna uppdateras medan du skriver.
      Gå till dem med piltangenterna.</p>
    <div class="search-modal__body" data-body></div>
    <p class="sr-only" role="status" data-live></p>`;

  document.body.append(element);

  dialog = element;
  input = requireEl<HTMLInputElement>("[data-input]", element);
  body = requireEl<HTMLElement>("[data-body]", element);
  live = requireEl<HTMLElement>("[data-live]", element);

  requireEl("[data-close]", element).addEventListener("click", () => element.close());

  // Both paths into "go", because they are different events on different devices: the phone's
  // on-screen keyboard has a Search key that fires submit, and a physical keyboard fires keydown.
  // The keydown sits on the field and not on the dialog, which is the fix for the first version:
  // a dialog-wide handler cancelled every Enter, so Enter on "Stäng" did not close the dialog and
  // Enter on a hit did not follow it. Cancelling here also stops a browser that does submit
  // implicitly from running "go" a second time.
  requireEl<HTMLFormElement>("[data-form]", element).addEventListener("submit", (event) => {
    event.preventDefault();
    go();
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      go();
    }
  });

  input.addEventListener("input", () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(ask, DEBOUNCE);
  });

  element.addEventListener("keydown", move);

  // Clicking the backdrop is outside the dialog's own box, so it lands on the dialog itself.
  element.addEventListener("click", (event) => {
    if (event.target === element) {
      element.close();
    }
  });

  element.addEventListener("close", () => {
    inFlight?.abort();
    trigger?.focus();
  });
}

/** Every hit link in the order it stands, for the arrow keys. */
function rows(): HTMLAnchorElement[] {
  return body === null ? [] : Array.from(body.querySelectorAll<HTMLAnchorElement>(".search-hit__link"));
}

/** Takes the query to the results page. A hit is followed by opening its link. */
function go(): void {
  const query = input?.value.trim() ?? "";
  if (query.length > 0) {
    window.location.assign(`/sok?q=${encodeURIComponent(query)}`);
  }
}

/**
 * Moves focus between the field and the hits; Enter and Escape are left to the browser.
 *
 * <p>The field is position 0 and the hits 1..n on the same ring, so both ends of the list lead
 * back to the field instead of a keyboard running aground on the last hit.</p>
 */
function move(event: KeyboardEvent): void {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
    return;
  }

  const field = input;
  const links = rows();
  if (field === null || links.length === 0) {
    return;
  }

  event.preventDefault();

  const stops: HTMLElement[] = [field, ...links];
  const here = stops.indexOf(document.activeElement as HTMLElement);
  const step = event.key === "ArrowDown" ? 1 : -1;
  const next = here < 0
    ? (step === 1 ? 1 : stops.length - 1)
    : (here + step + stops.length) % stops.length;

  stops[next]?.focus();
  if (next > 0) {
    stops[next]?.scrollIntoView({ block: "nearest" });
  }
}

/** Asks the backend and redraws the list. Another letter aborts the request in flight. */
async function ask(): Promise<void> {
  const query = input?.value.trim() ?? "";

  inFlight?.abort();
  inFlight = new AbortController();

  try {
    const url = query.length >= MIN_QUERY ? `/api/search?q=${encodeURIComponent(query)}` : "/api/search";
    const response = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: inFlight.signal,
    });

    if (!response.ok) {
      render(null);
      return;
    }

    render((await response.json()) as Results);
  } catch {
    // An aborted request is the normal case between keystrokes, not a failure worth showing.
    if (inFlight?.signal.aborted !== true) {
      render(null);
    }
  }
}

/** Writes out the groups, or what should stand there when there are none. */
function render(results: Results | null): void {
  if (body === null || live === null) {
    return;
  }

  // A redraw throws away whatever element focus was on. Only the field can hold focus while
  // someone types, but a failed request can land after the arrow keys have moved into the list,
  // and focus falling to <body> inside an open dialog leaves a keyboard with nowhere to go.
  const hadFocus = body.contains(document.activeElement);

  if (results === null) {
    body.innerHTML = `<p class="search-modal__note">Söket svarar inte just nu.
      Prova igen, eller <a href="/kontakt-bokning/">hör av dig till oss</a>.</p>`;
    live.textContent = "Söket svarar inte.";
    if (hadFocus) {
      input?.focus();
    }
    return;
  }

  if (results.groups.length === 0) {
    const query = input?.value.trim() ?? "";
    body.innerHTML = `<p class="search-modal__note">Inga träffar på ”${escape(query)}”.
      Prova ett kortare ord.</p>`;
    live.textContent = `Inga träffar på ${query}.`;
    if (hadFocus) {
      input?.focus();
    }
    return;
  }

  // The list carries its heading as its accessible name: the arrow keys move focus straight into
  // it without passing the <h2>, so without this a screen reader enters an unnamed "list, 4 items".
  body.innerHTML = results.groups.map((group, index) => `
    <section class="search-group">
      <h2 class="search-group__title" id="search-modal-group-${index}">${escape(group.heading)}</h2>
      <ul class="search-hits" aria-labelledby="search-modal-group-${index}">${group.hits.map(row).join("")}</ul>
    </section>`).join("");

  const shown = results.groups.reduce((count, group) => count + group.hits.length, 0);
  live.textContent = results.suggested
    ? `${shown} förslag.`
    : `${results.total} ${results.total === 1 ? "träff" : "träffar"}.`;

  if (hadFocus) {
    input?.focus();
  }
}

/** One hit row. The picture is skipped when the page has none. */
function row(hit: Hit): string {
  const image = hit.thumbnail.url.length > 0
    ? `<span class="search-hit__frame"><img src="${escape(hit.thumbnail.url)}" alt=""
         width="${hit.thumbnail.width}" height="${hit.thumbnail.height}" loading="lazy" decoding="async"></span>`
    : "";

  const summary = hit.summary.length > 0
    ? `<span class="search-hit__summary">${escape(hit.summary)}</span>`
    : "";

  return `<li class="search-hit">
    <a class="search-hit__link" href="${escape(hit.url)}">
      ${image}
      <span class="search-hit__body">
        <span class="search-hit__title">${escape(hit.title)}</span>
        ${summary}
      </span>
    </a>
  </li>`;
}

/**
 * Escapes text before it is set as HTML.
 *
 * <p>The content comes from our own backend, but it comes out of an editor's field and goes in
 * through innerHTML. A quotation mark in the name of an accommodation has to become a quotation
 * mark and not a broken attribute.</p>
 */
function escape(value: string): string {
  const node = document.createElement("span");
  node.textContent = value;
  return node.innerHTML.replace(/"/g, "&quot;");
}

/** Opens the dialog, empties the field and fetches the suggestions. */
function open(button: HTMLElement): void {
  if (dialog === null) {
    build();
  }
  if (dialog === null || input === null || body === null) {
    return;
  }

  trigger = button;
  input.value = "";
  body.innerHTML = "";
  dialog.showModal();
  input.focus();
  void ask();
}

export function initSearch(): void {
  const button = find("[data-search-open]");
  if (button === null) {
    return;
  }

  button.addEventListener("click", () => open(button));
}
